import type { FormField, KycFieldId } from '@funkit/connect-core'
import { isValueField } from '@funkit/connect-core'
import type { TextInputProps } from 'react-native'

/**
 * The pages the US form is drawn as, in the order the design steps them.
 *
 * Figma "EDIT: KYC Enhancements": 1508:2616 (name), 1844:17471 (date of
 * birth), 1631:7256 (address), 1631:7883 (SSN). `DETAILS` is not a frame — it
 * is where a field the design never drew goes, so a form the server words its
 * own way still renders.
 */
export type KycSection =
  | 'NAME'
  | 'DOB'
  | 'NATIONALITY'
  | 'COUNTRY'
  | 'ADDRESS'
  | 'SSN'
  | 'PHONE'
  | 'OCCUPATION'
  | 'EMPLOYMENT'
  | 'PURPOSE'
  | 'SOURCE_OF_WEALTH'
  | 'INCOME'
  | 'ACTIVITY'
  | 'DETAILS'

export interface KycSectionPage {
  section: KycSection
  /** Drawn as one card with a rule between rows, rather than loose boxes. */
  grouped: boolean
  /** Fields sharing a row sit side by side, as the design pairs them. */
  rows: FormField[][]
}

/**
 * The questionnaire the due-diligence figures arrive under. The contract types
 * a questionnaire id as a template, so these are the three this build draws
 * a page for; any other lands in `DETAILS`.
 */
export const GROSS_ANNUAL_INCOME =
  'QUESTIONNAIRE_RESPONSE:customerDueDiligence:grossAnnualIncome'
export const EXPECTED_AMOUNT_PER_MONTH =
  'QUESTIONNAIRE_RESPONSE:customerDueDiligence:expectedTransactionAmountPerMonth'
export const TRANSACTIONS_PER_MONTH =
  'QUESTIONNAIRE_RESPONSE:customerDueDiligence:transactionFrequencyPerMonth'

/**
 * Which page a field belongs to, by id.
 *
 * The contract closes the id set, so these are the canonical names and there
 * is no second spelling to carry. An id with no page here lands in `DETAILS`
 * rather than being refused: the server owns the list, and a form is not
 * undrawable just because it asks for something the frames do not group.
 */
const SECTION_BY_ID: Readonly<Partial<Record<KycFieldId, KycSection>>> = {
  // 1508:2616 — "First name", "M.I.", "Last Name".
  LEGAL_FIRST_NAME: 'NAME',
  LEGAL_MIDDLE_NAME: 'NAME',
  LEGAL_LAST_NAME: 'NAME',
  // 1844:17471 — its own page, asked for on its own over the date picker.
  DATE_OF_BIRTH: 'DOB',
  NATIONALITY: 'NATIONALITY',
  // 1631:7256 — "Address", "Apartment, suite, etc.", "City", "State", "ZIP code".
  RESIDENTIAL_ADDRESS_LINE_1: 'ADDRESS',
  RESIDENTIAL_ADDRESS_LINE_2: 'ADDRESS',
  RESIDENTIAL_ADDRESS_CITY: 'ADDRESS',
  RESIDENTIAL_ADDRESS_REGION: 'ADDRESS',
  RESIDENTIAL_ADDRESS_POSTAL_CODE: 'ADDRESS',
  RESIDENTIAL_ADDRESS_COUNTRY: 'COUNTRY',
  // 1631:7883 — "SSN".
  TAX_IDENTIFIER: 'SSN',
  TAX_RESIDENCE_COUNTRY: 'COUNTRY',
  OCCUPATION_TITLE: 'OCCUPATION',
  OCCUPATION_INDUSTRY: 'OCCUPATION',
  PURPOSE_OF_TRANSACTION: 'PURPOSE',
  PURPOSE_OF_SERVICE: 'PURPOSE',
  // MoonPay's customer due diligence round, asked after the SSN.
  PHONE_NUMBER: 'PHONE',
  EMPLOYMENT_STATUS: 'EMPLOYMENT',
  SOURCE_OF_WEALTH_DECLARATION: 'SOURCE_OF_WEALTH',
  [GROSS_ANNUAL_INCOME]: 'INCOME',
  [EXPECTED_AMOUNT_PER_MONTH]: 'ACTIVITY',
  [TRANSACTIONS_PER_MONTH]: 'ACTIVITY',
}

/**
 * The keyboard a typed field raises. The SSN page is drawn over a number pad
 * (1631:7883) and the due-diligence figures over the pads that fit them;
 * every other page in the form is drawn over letters.
 */
const KEYBOARD_BY_ID: Readonly<
  Partial<Record<KycFieldId, TextInputProps['keyboardType']>>
> = {
  TAX_IDENTIFIER: 'number-pad',
  PHONE_NUMBER: 'phone-pad',
  [GROSS_ANNUAL_INCOME]: 'decimal-pad',
  [EXPECTED_AMOUNT_PER_MONTH]: 'decimal-pad',
  [TRANSACTIONS_PER_MONTH]: 'number-pad',
}

/**
 * Field types a page can draw. A date and a country are typed into the same
 * filled box as any other answer; a hosted step is an errand the user runs
 * elsewhere and a document streams to an endpoint this build has not
 * installed, so neither has a box to draw.
 */
const DRAWN_TYPES = new Set(['TEXT', 'SELECT', 'DATE', 'COUNTRY'])

/** Fields the design draws beside the one before them (1508:2616, "row"). */
const SHARES_ROW_WITH_PREVIOUS = new Set<KycFieldId>([
  'RESIDENTIAL_ADDRESS_POSTAL_CODE',
])

/** One card ruled between its rows (1631:7256, "address"). */
const GROUPED_SECTIONS = new Set<KycSection>(['ADDRESS'])

/**
 * The order 1508:2616 → 1844:17471 → 1631:7256 → 1631:7883 walks, anything
 * else trailing.
 */
const SECTION_ORDER: readonly KycSection[] = [
  'NAME',
  'DOB',
  'NATIONALITY',
  'COUNTRY',
  'ADDRESS',
  'SSN',
  'PHONE',
  'OCCUPATION',
  'EMPLOYMENT',
  'PURPOSE',
  'SOURCE_OF_WEALTH',
  'INCOME',
  'ACTIVITY',
  'DETAILS',
]

/**
 * The page's field a date picker can answer, when that is the whole page.
 *
 * A picker takes the keyboard's slot, so it can only be offered on a page
 * with nothing else to type: a date beside two names would leave one of the
 * two controls covering the other. Anywhere else the date is typed.
 */
const IDENTITY_SECTIONS: ReadonlySet<KycSection> = new Set<KycSection>([
  'NAME',
  'DOB',
  'NATIONALITY',
  'COUNTRY',
  'ADDRESS',
  'SSN',
])

/**
 * The server owns the field list, so an id this build does not know is not
 * identity: only the frames that collect who someone is qualify.
 */
export function isIdentityField(field: FormField): boolean {
  const section = SECTION_BY_ID[field.id]
  return section !== undefined && IDENTITY_SECTIONS.has(section)
}

/**
 * The pages that draw their answers on the page rather than behind a chooser
 * (2475:8992).
 *
 * Named, not inferred from the field's type: `DETAILS` also collects a
 * `SELECT`, but it holds whatever the server asks for that no frame drew, and
 * a list of unknown length is what the chooser's sheet is for.
 */
const INLINE_CHOICE_SECTIONS: ReadonlySet<KycSection> = new Set<KycSection>([
  'OCCUPATION',
  'EMPLOYMENT',
  'PURPOSE',
  'SOURCE_OF_WEALTH',
])

export function isInlineChoiceSection(section: KycSection): boolean {
  return INLINE_CHOICE_SECTIONS.has(section)
}

export function pickedDateField(page: KycSectionPage): FormField | undefined {
  const fields = page.rows.flat()
  const only = fields.length === 1 ? fields[0] : undefined
  return only?.type === 'DATE' ? only : undefined
}

/**
 * Whether the field is typed into, and so takes a turn in the return-key
 * chain. A choice is not.
 */
export function isFocusableField(field: FormField): boolean {
  return field.type === 'TEXT' || field.type === 'DATE'
}

export function fieldKeyboardType(
  field: FormField,
): TextInputProps['keyboardType'] | undefined {
  return field.type === 'TEXT' ? KEYBOARD_BY_ID[field.id] : undefined
}

/** Whether the field raises a pad rather than letters, and so has no return key. */
export function isDigitsField(field: FormField): boolean {
  return fieldKeyboardType(field) !== undefined
}

/** Whether the field is drawn at a fixed width rather than growing to fill. */
export function isFixedWidthField(field: FormField): boolean {
  return field.id === 'LEGAL_MIDDLE_NAME'
}

/**
 * The form's fields that no page draws.
 *
 * Type only: an unknown id has a page (`DETAILS`), so refusing one would
 * reject a whole form over a field the screens can draw perfectly well. A
 * form with any of these cannot be completed here, so the caller shows its
 * unsupported state rather than a form missing a field the server requires.
 */
export function unsupportedFields(fields: readonly FormField[]): FormField[] {
  return fields.filter((field) => !DRAWN_TYPES.has(field.type))
}

/**
 * Splits a form into the pages the design steps through.
 *
 * Pages keep the design's order; fields keep the server's, so a page that
 * gains a field it already draws renders it without a client release. A page
 * with no fields is not a page.
 */
export function sectionPages(fields: readonly FormField[]): KycSectionPage[] {
  const bySection = new Map<KycSection, FormField[]>()
  for (const field of fields) {
    if (!DRAWN_TYPES.has(field.type)) {
      continue
    }
    const section = SECTION_BY_ID[field.id] ?? 'DETAILS'
    const collected = bySection.get(section) ?? []
    collected.push(field)
    bySection.set(section, collected)
  }

  return SECTION_ORDER.flatMap<KycSectionPage>((section) => {
    const collected = bySection.get(section)
    if (collected === undefined) {
      return []
    }
    if (isInlineChoiceSection(section) || section === 'DETAILS') {
      return collected.map((field) => ({
        section,
        grouped: false,
        rows: [[field]],
      }))
    }
    const grouped = GROUPED_SECTIONS.has(section)
    return [
      {
        section,
        grouped,
        // A card stacks: its rule already separates the fields, so nothing
        // pairs inside one.
        rows: pairRows(collected),
      },
    ]
  })
}

/**
 * A field only joins the row before it when the design pairs the two — and
 * never when it is the first field of the page, which the design never draws
 * beside anything.
 */
function pairRows(fields: readonly FormField[]): FormField[][] {
  const rows: FormField[][] = []
  for (const field of fields) {
    const previous = rows[rows.length - 1]
    const ridesAlong =
      previous !== undefined &&
      SHARES_ROW_WITH_PREVIOUS.has(field.id) &&
      previous[0]?.id === 'RESIDENTIAL_ADDRESS_REGION' &&
      isValueField(field) &&
      previous.every(isValueField)
    if (ridesAlong) {
      previous.push(field)
    } else {
      rows.push([field])
    }
  }
  return rows
}
